import React, { useEffect, useState } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@capacitor/inappbrowser';
import VersionUpdateModal from './VersionUpdateModal';
import { useTranslation } from 'react-i18next';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';
import { API_BASE_URL } from '../config/onlineConfig';

const VersionCheckManager = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [versionData, setVersionData] = useState(null);
    const { t } = useTranslation();

    useEffect(() => {
        // Run check on mount
        checkVersion();

        // Also listen for app resume (foreground)
        const listener = App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                checkVersion();
            }
        });

        return () => {
            listener.then(handle => handle.remove());
        };
    }, []);

    const checkVersion = async () => {
        // Avoid checking updates on web version
        if (Capacitor.getPlatform() === 'web') {
            return;
        }

        debugLog('🔍 Checking for updates...');

        try {
            // 1. Get current app version
            const appInfo = await App.getInfo();
            const currentVersion = appInfo.version;

            // 2. Fetch remote version info
            const platform = Capacitor.getPlatform();
            const response = await fetch(`${API_BASE_URL}/app-version?platform=${platform}`);
            if (!response.ok) {
                if (response.status === 404) {
                    debugLog('ℹ️ No version info published for this platform yet');
                    return;
                }
                throw new Error('Failed to fetch version info');
            }

            const data = await response.json();
            const latestVersion = data.version;

            debugLog(`ℹ️ Current: ${currentVersion}, Latest: ${latestVersion}`);

            if (compareVersions(latestVersion, currentVersion) > 0) {
                debugLog('✨ New version available');

                // Check for snooze (unless forced)
                if (!data.force_update) {
                    const snoozeTime = localStorage.getItem('version_check_snooze');
                    if (snoozeTime && Date.now() < parseInt(snoozeTime)) {
                        debugLog('💤 Update snoozed');
                        return;
                    }
                }

                setVersionData(data);
                setModalOpen(true);
            } else {
                debugLog('✅ App is up to date');
            }

        } catch (error) {
            console.warn('⚠️ Version check failed:', error);
        }
    };

    const compareVersions = (v1, v2) => {
        // v1: remote (latest), v2: current
        if (!v1 || !v2) return 0;

        const v1Parts = v1.split('.').map(Number);
        const v2Parts = v2.split('.').map(Number);

        for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
            const val1 = v1Parts[i] || 0;
            const val2 = v2Parts[i] || 0;

            if (val1 > val2) return 1;
            if (val1 < val2) return -1;
        }

        return 0;
    };

    const handleUpdate = async (data) => {
        const storeUrl = data.url;

        if (!storeUrl) {
            console.error('No update URL found');
            return;
        }

        try {
            // Use system browser/store
            await InAppBrowser.openInExternalBrowser({ url: storeUrl });
        } catch (e) {
            // Fallback
            window.open(storeUrl, '_system');
        }
    };

    const handleLater = () => {
        // Snooze for 24 hours
        const snoozeDuration = 24 * 60 * 60 * 1000;
        localStorage.setItem('version_check_snooze', (Date.now() + snoozeDuration).toString());
        setModalOpen(false);
    };

    return (
        <VersionUpdateModal
            open={modalOpen}
            versionData={versionData}
            onUpdate={handleUpdate}
            onLater={handleLater}
        />
    );
};

export default VersionCheckManager;
